import { useEffect } from 'react';
import { logAccess, reportError } from '@/observability/clientLogger';

/**
 * When shell-app runs inside superagent's Tauri iframe (mobile),
 * window.__TAURI__ is not available cross-origin. This component
 * creates a fake __TAURI__ that sends download requests to the
 * parent superagent window via postMessage. The parent handles
 * the actual Tauri download_file_v2 + share_file_v2 calls.
 *
 * iOS WKWebView blocks parent→cross-origin-iframe postMessage, so
 * the old PING/PONG handshake never completed on iOS.
 *
 * Fix: detect Tauri by checking document.referrer and the URL origin.
 * tauri://localhost and https://tauri.localhost are the two origins
 * Tauri uses for the shell WebView — if we were loaded from there,
 * we're inside Tauri and should install the bridge immediately,
 * no PONG needed.
 *
 * Android still works via PING/PONG as a fallback (parent→child
 * postMessage works on Android WebView).
 */
function showDownloadToast(filename: string) {
  const name = filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
  const toast = document.createElement('div');
  toast.style.cssText = 'position:fixed;top:16px;left:0;right:0;margin:0 auto;width:fit-content;background:rgba(76,175,80,0.95);color:#fff;padding:10px 20px;border-radius:24px;box-shadow:0 4px 20px rgba(0,0,0,0.2);z-index:2147483647;font-family:-apple-system,sans-serif;font-size:14px;text-align:center;max-width:90vw;display:flex;align-items:center;gap:8px;';
  toast.innerHTML = '<span style="font-size:16px;">✅</span><span>Downloaded ' + name + '</span>';
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

/*
 * There was an isTauriParent() probe here — referrer scheme first, then
 * location.ancestorOrigins as an Android-only fallback, since iOS WKWebView does not
 * implement it. Nothing ever called it: installBridge() runs unconditionally (see the
 * note above it), because a bridge installed under a non-Tauri parent is harmless —
 * TAURI_DOWNLOAD_RESULT never arrives, the 30s timeout fires and the browser fallback
 * takes over. Detection that cannot work on iOS is not worth the branch it guards.
 */

export default function TauriBridge() {
  useEffect(() => {
    const w = window as any;
    // Already has real Tauri (shouldn't happen cross-origin, but guard anyway)
    if (w.__TAURI__?.core?.invoke || w.tauri?.invoke) return;
    // Not inside an iframe
    if (!window.parent || window.parent === window) return;

    let installed = false;

    const installBridge = () => {
      if (installed) return;
      installed = true;

      const proxyInvoke = (command: string, args?: any): Promise<any> => {
        if (command === 'download_file_v2') {
          return new Promise((resolve) => {
            const handler = (ev: MessageEvent) => {
              if (ev.data?.type === 'TAURI_DOWNLOAD_RESULT' && ev.data?.filename === args?.filename) {
                window.removeEventListener('message', handler);
                // Filename only, never the payload or a signed URL.
                logAccess('shell_native_download_result', { success: !!ev.data.success, filename: args?.filename });
                // Show toast inside iframe on Android (iOS uses native share sheet)
                if (ev.data.showToast && ev.data.success && /Android/i.test(navigator.userAgent)) {
                  showDownloadToast(args.filename);
                }
                if (ev.data.success) {
                  resolve(`File downloaded successfully to: ${args.filename}`);
                } else {
                  resolve(Promise.reject(new Error(ev.data.error || 'Download failed')));
                }
              }
            };
            window.addEventListener('message', handler);
            setTimeout(() => {
              window.removeEventListener('message', handler);
              // A timeout here leaves the user staring at a control that did
              // nothing — the native side never answered, and no request of ours
              // failed, so nothing else in the system records it.
              reportError(new Error('Native download timed out'), {
                event_detail: 'native_download_timeout', filename: args?.filename,
              });
              resolve(Promise.reject(new Error('Download timed out')));
            }, 30000);
            window.parent.postMessage({
              type: 'TAURI_DOWNLOAD_REQUEST',
              url: args?.url,
              filename: args?.filename
            }, '*');
          });
        }
        if (command === 'share_file_v2') {
          return Promise.resolve('handled_by_parent');
        }
        if (command === 'ios_share_url') {
          // Forward to superagent as a TAURI_DOWNLOAD_REQUEST — superagent detects iOS
          // and calls the real ios_share_url Rust command to open the share sheet.
          return new Promise((resolve) => {
            const handler = (ev: MessageEvent) => {
              if (ev.data?.type === 'TAURI_DOWNLOAD_RESULT' && ev.data?.filename === args?.filename) {
                window.removeEventListener('message', handler);
                resolve(ev.data.success ? 'share sheet opened' : '');
              }
            };
            window.addEventListener('message', handler);
            setTimeout(() => { window.removeEventListener('message', handler); resolve(''); }, 30000);
            window.parent.postMessage({
              type: 'TAURI_DOWNLOAD_REQUEST',
              url: args?.url,
              filename: args?.filename
            }, '*');
          });
        }
        return Promise.reject(new Error('Command not supported via bridge: ' + command));
      };

      w.__TAURI__ = { core: { invoke: proxyInvoke } };
    };

    // Always install the bridge when inside any iframe.
    // iOS WKWebView strips cross-origin referrers and blocks parent→child postMessage,
    // so referrer/PING-PONG detection is unreliable on real devices.
    // Child→parent postMessage always works. If the parent is not Tauri,
    // TAURI_DOWNLOAD_RESULT never arrives → 30s timeout → downloadBlob returns false
    // → browser fallback runs. Zero cost on non-Tauri environments.
    installBridge();

    // Relay layer: Apps are loaded inside shell-app which is itself inside superagent.
    // The nesting is: superagent (Tauri top) → shell-app iframe → App iframe.
    // When an App calls window.parent.postMessage(TAURI_DOWNLOAD_REQUEST), it reaches
    // shell-app — NOT superagent. Shell-app must forward it one level up to superagent,
    // and relay TAURI_DOWNLOAD_RESULT back down to the originating App iframe.
    const pendingDownloads = new Map<string, MessageEventSource>();

    const relayHandler = (ev: MessageEvent) => {
      if (!ev.data?.type) return;

      if (ev.data.type === 'TAURI_DOWNLOAD_REQUEST') {
        // From App child → forward up to superagent parent
        if (ev.source) pendingDownloads.set(ev.data.filename, ev.source);
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(ev.data, '*');
        }
      } else if (ev.data.type === 'TAURI_DOWNLOAD_RESULT') {
        // From superagent parent → forward down to the originating App iframe
        const source = pendingDownloads.get(ev.data.filename);
        if (source) {
          (source as Window).postMessage(ev.data, '*');
          pendingDownloads.delete(ev.data.filename);
        }
      }
    };

    window.addEventListener('message', relayHandler);

    return () => {
      window.removeEventListener('message', relayHandler);
      if (w.__TAURI__?.core?.invoke?.toString?.().includes('proxyInvoke')) {
        delete w.__TAURI__;
      }
    };
  }, []);

  return null;
}
